/**
 * Khối media trên PropertyCard — carousel ảnh với fade và nút prev/next.
 *
 * Sắp xếp ảnh cover trước; placeholder khi không có ảnh.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { TEXT } from '../../shared/languages';

const placeholder =
  'https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=900&q=80';

const ChevronIcon = ({ direction }) => (
  <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    {direction === 'prev' ? (
      <path d="M20 8l-8 8 8 8" strokeLinecap="round" strokeLinejoin="round" />
    ) : (
      <path d="M12 8l8 8-8 8" strokeLinecap="round" strokeLinejoin="round" />
    )}
  </svg>
);

/** Trích URL ảnh đã sắp xếp từ mảng images API. */
const getPropertyImageUrls = (images = []) => {
  if (!images.length) {
    return [placeholder];
  }

  return [...images]
    .sort(
      (firstImage, secondImage) =>
        Number(secondImage.is_cover) - Number(firstImage.is_cover) ||
        (firstImage.order ?? 0) - (secondImage.order ?? 0)
    )
    .map((image) => image.url)
    .filter(Boolean);
};

/**
 * Media carousel trên thẻ BĐS.
 * @param {Object} props
 * @param {number|string} props.propertyId
 * @param {string} props.title
 * @param {Array} [props.images]
 * @param {string} props.detailPath
 * @param {boolean} [props.showBadge]
 * @param {string} [props.badgeLabel]
 */
export const PropertyCardMedia = ({
  propertyId,
  title,
  images = [],
  detailPath,
  showBadge = false,
  badgeLabel = '',
}) => {
  const common = TEXT.common;
  const imageUrls = useMemo(() => getPropertyImageUrls(images), [images]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isImageVisible, setIsImageVisible] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);
  const hasMultipleImages = imageUrls.length > 1;
  const currentImageUrl = imageUrls[activeIndex] || placeholder;

  useEffect(() => {
    setActiveIndex(0);
    setIsImageVisible(true);
    setIsAnimating(false);
  }, [propertyId, imageUrls]);

  const showPhoto = (event, nextIndex) => {
    event.preventDefault();
    event.stopPropagation();

    if (isAnimating) {
      return;
    }

    const total = imageUrls.length;
    const nextActiveIndex = (nextIndex + total) % total;

    if (nextActiveIndex === activeIndex) {
      return;
    }

    setIsAnimating(true);
    setIsImageVisible(false);

    window.setTimeout(() => {
      setActiveIndex(nextActiveIndex);
      setIsImageVisible(true);

      window.setTimeout(() => {
        setIsAnimating(false);
      }, 320);
    }, 180);
  };

  return (
    <>
      <Link to={detailPath} className="property-image-link">
        <img
          src={currentImageUrl}
          alt={title}
          className={`property-image ${isImageVisible ? 'is-visible' : 'is-fading'}`}
          loading="lazy"
        />
        {showBadge && badgeLabel ? <span className="property-card-badge">{badgeLabel}</span> : null}
      </Link>

      {hasMultipleImages ? (
        <>
          <button
            type="button"
            className="property-image-nav property-image-nav--prev"
            aria-label={common.previous}
            onClick={(event) => showPhoto(event, activeIndex - 1)}
          >
            <ChevronIcon direction="prev" />
          </button>
          <button
            type="button"
            className="property-image-nav property-image-nav--next"
            aria-label={common.next}
            onClick={(event) => showPhoto(event, activeIndex + 1)}
          >
            <ChevronIcon direction="next" />
          </button>
          <div className="property-image-dots" aria-hidden="true">
            {imageUrls.map((imageUrl, index) => (
              <span
                key={`${propertyId}-${imageUrl}-${index}`}
                className={index === activeIndex ? 'is-active' : ''}
              />
            ))}
          </div>
        </>
      ) : null}
    </>
  );
};
